import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import DotAndText from 'components/atoms/DotAndText'
import Icon from 'components/atoms/Icon'
import { BlackParagraph } from 'components/atoms/Paragraph'
import { LinkTo } from 'components/atoms/Pointer'
import Table from 'components/organisms/Table'
import Header from 'components/organisms/Table/atoms/Header'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  Column,
  SortingRule,
  useExpanded,
  useFlexLayout,
  useGlobalFilter,
  usePagination,
  useResizeColumns,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table'
import { toast } from 'react-toastify'
import { arrayToCSV, emptyArray, sortByAlphabet, sortByDate } from 'utils/array'
import { highlight } from 'utils/highlight'
import { DATE_TIME_FORMAT } from 'utils/date'
import Tabs, { Tab } from 'components/molecules/Tabs'
import { MODALS } from 'components/organisms/Modal'
import { Dropdown } from 'components/molecules/Dropdown'
import { MenuItem, Popup } from 'components/molecules/Popup'
import { IconButton } from 'components/molecules/Button'
import Button from 'components/molecules/Button'
import { deleteUserFn, getFeatureGroupsFn, getPendingUsersFn, getUsersFn, importUsersFn } from 'api'
import { useConfirm } from 'components/organisms/ConfirmDialog'
import { Page, PageHeader } from 'components/organisms/layout'
import { useLocalStorage } from 'hooks/useLocalStorage'
import { UserWithFG, UsersGlobalFilter } from './types'
import { useUserContext } from 'contexts/UserContext'
import { downloadCsv } from 'utils/download'
import { prepareUsersCsvData } from './utils'
import { AdvancedFilters } from './components'
import { fromPairs } from 'lodash'
import { FeatureGroup, User } from '../../@types'
import { Checkbox } from 'components/molecules/Checkbox'
import { CellProps } from 'react-table'

const columns: Column<UserWithFG>[] = [
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.user')} />
    },
    accessor: 'name',
    sortType: (a, b) => sortByAlphabet(a.values.name, b.values.name),
    Cell: ({ value, state: { globalFilter } }) => (
      <BlackParagraph text={highlight(value, globalFilter.search)} size="12" />
    ),
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.status')} />
    },
    accessor: 'id',
    sortType: (a, b) => sortByDate(a.values.last_login, b.values.last_login),
    Cell: ({ row }) => {
      const isActive = Boolean(row.original.customer_id)
      const [t] = useTranslation()

      return (
        <DotAndText
          text={isActive ? t('common.active') : t('common.notActive')}
          color={isActive ? '#499976' : '#d06d6e'}
        />
      )
    },
    width: 60,
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.created')} />
    },
    accessor: 'created',
    sortType: (a, b) => sortByDate(a.values.created, b.values.created),
    Cell: ({ value }) => {
      const date = value ? dayjs(value as string).format(DATE_TIME_FORMAT) : '-'
      return <BlackParagraph text={date} size="12" />
    },
    width: 100,
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.lastLogin')} />
    },
    accessor: 'last_login',
    sortType: (a, b) => sortByDate(a.values.last_login, b.values.last_login),
    Cell: ({ value }) => {
      if (!value) return <div>-</div>
      const date = dayjs(value).format(DATE_TIME_FORMAT)
      return <BlackParagraph text={date} size="12" />
    },
    width: 100,
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.featureGroup')} />
    },
    accessor: 'featureGroup',
    sortType: (a, b) => sortByAlphabet(a.values.featureGroup, b.values.featureGroup),
    Cell: ({ value, state: { globalFilter } }) =>
      !value ? <div>-</div> : <BlackParagraph text={highlight(value, globalFilter.search)} size="12" />,
    width: 140,
  },
  {
    Header: () => null,
    accessor: 'name',
    id: 'edit',
    Cell: (cell) => {
      const [t] = useTranslation()
      const { name, customer_id, last_login, i_realm } = cell.row.original
      const isActive = Boolean(last_login)
      const hasSelection = Object.keys(cell.state.selectedRowIds).length > 0

      const queryClient = useQueryClient()
      const { confirm } = useConfirm()

      const { mutate: deleteUser } = useMutation(deleteUserFn, {
        onError: () => {
          toast.error(t('page.users.deleteError', { name }))
        },
        onSuccess: (a, b) => {
          queryClient.invalidateQueries(['users']).then(() => {
            //pass
          })
          queryClient.invalidateQueries(['pendingUsers']).then(() => {
            //pass
          })
          if (b) {
            toast.success(t('page.users.deleteSuccess', { name }))
          }
        },
      })
      if (hasSelection) return null
      return (
        // !hasSelection && (
        <Dropdown
          button={
            <IconButton>
              <Icon icon="uil:ellipsis-v" />
            </IconButton>
          }
        >
          <Popup>
            {isActive && (
              <LinkTo to={`/users/${customer_id}?modal=${MODALS.editUser}`}>
                <MenuItem value="change-fg" icon={<Icon icon="uil:edit" />} onClick={() => {}}>
                  {t('common.edit')}
                </MenuItem>
              </LinkTo>
            )}
            <MenuItem
              value="delete"
              red
              icon={<Icon icon="uil:trash" />}
              onClick={async () => {
                const isConfirmed = await confirm(
                  <p style={{ width: '210px' }}>
                    {t('page.users.reallyDelete')} <b>{name}</b>
                  </p>
                )
                if (isConfirmed) {
                  deleteUser({ username: name, i_realm })
                }
              }}
            >
              {t('common.delete')}
            </MenuItem>
          </Popup>
        </Dropdown>
      )
    },
    width: 30,
    disableSortBy: true,
  },
]

const Users: FC = () => {
  const [t] = useTranslation()
  const { state } = useUserContext()
  const { i_realm } = state?.realm
  const [storedStatus, setStatusToStorage] = useLocalStorage('users-status')
  const [pagination, setPagination] = useState(() => ({ pageIndex: 0, pageSize: 50 }))
  const [sortBy, setSortBy] = useState<SortingRule<User>[]>(() => [{ id: 'created', desc: true }])

  const initialGlobalFilter: UsersGlobalFilter = {
    feature_profile: [],
    search: '',
    status: storedStatus || 'active',
    OS: '',
    version: '',
    lastLogin: '',
    cabinet: '',
    role: '',
  }

  const [globalFilterCopy, setGlobalFilterCopy] = useState<UsersGlobalFilter>(() => initialGlobalFilter)

  const uploadRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!storedStatus) {
      setStatusToStorage('active')
    }
  }, [storedStatus, setStatusToStorage])

  const { data: featureGroups, isLoading: featureGroupIsLoading } = useQuery(
    ['feature-groups', i_realm],
    getFeatureGroupsFn,
    {
      select: (data) => fromPairs(data.map((fg) => [fg.i_featuregroup, fg])) as Record<string, FeatureGroup>,
    }
  )

  const { data: activeUsers, isLoading: activeUsersIsLoading } = useQuery(
    ['users', i_realm, pagination, sortBy[0], globalFilterCopy],
    getUsersFn,
    {
      enabled: !!i_realm && storedStatus === 'active' && !featureGroupIsLoading, //&& Object.values(featureGroups || {}).length === 0
      select: (data) => ({
        total: data.total,
        items: data.items.map((item) => ({
          ...item,
          featureGroup: (featureGroups || {})[item.i_featuregroup]?.name,
        })) as UserWithFG[],
      }),
      keepPreviousData: false,
    }
  )

  const { data: pendingUsers, isLoading: pendingUsersLoading } = useQuery(
    ['pendingUsers', i_realm, pagination, sortBy[0], globalFilterCopy],
    getPendingUsersFn,
    {
      enabled: !!i_realm && storedStatus === 'pending' && !featureGroupIsLoading, //&& Object.values(featureGroups || {}).length === 0
      select: (data) => ({
        total: data.total,
        items: data.items.map((item) => ({
          ...item,
          featureGroup: (featureGroups || {})[item.i_featuregroup]?.name,
        })) as UserWithFG[],
      }),
      keepPreviousData: false,
    }
  )

  const users = storedStatus === 'active' ? activeUsers : pendingUsers
  const isLoading = storedStatus === 'active' ? activeUsersIsLoading : pendingUsersLoading

  const pageCount = useMemo(
    () => Math.ceil(((users?.total as number) || 0) / pagination.pageSize) || 0,
    [users?.total, pagination.pageSize]
  )

  const tableProps = useTable(
    {
      columns,
      data: users?.items ?? emptyArray,
      initialState: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        globalFilter: initialGlobalFilter,
      },
      autoResetPage: false,
      disableSortRemove: true,
      manualPagination: true,
      manualSortBy: true,
      manualGlobalFilter: true,
      matablePropsnualGlobalFilter: true,
      pageCount: pageCount,
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useFlexLayout,
    useResizeColumns,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        {
          id: 'selection',
          Header: ({ getToggleAllRowsSelectedProps }) => {
            const props = getToggleAllRowsSelectedProps()

            return <Checkbox {...props} />
          },
          width: 40,
          Cell: ({ row }: CellProps<UserWithFG>) => {
            const props = row.getToggleRowSelectedProps()

            return <Checkbox {...props} />
          },
        },
        ...columns,
      ])
    }
  )

  const selectedRows = tableProps.selectedFlatRows
  const selectedUsers = selectedRows.map((row) => row.original.name)

  const { confirm } = useConfirm()
  const queryClient = useQueryClient()

  // Bulk deletion of selected users
  const handleBulkDelete = async () => {
    if (!selectedUsers.length) return

    const confirmed = await confirm(t('page.users.confirmDeleteMultiple', { count: selectedUsers.length }))

    if (!confirmed) return

    try {
      await Promise.all(selectedUsers.map((username) => deleteUserFn({ username, i_realm })))

      toast.success(t('page.users.deleteMultipleSuccess', { count: selectedUsers.length }))

      queryClient.invalidateQueries(['users'])
      queryClient.invalidateQueries(['pendingUsers'])

      tableProps.toggleAllRowsSelected(false)
    } catch {
      toast.error(t('error.default'))
    }
  }

  const originalSetGlobalFilter = tableProps.setGlobalFilter
  const selectedCount = tableProps.selectedFlatRows.length

  useEffect(() => {
    const { pageIndex, pageSize, sortBy: _sortBy } = tableProps.state

    if (pageIndex !== pagination.pageIndex || pageSize !== pagination.pageSize) {
      setPagination({ pageIndex, pageSize })
    }

    if (_sortBy[0]) {
      const item = _sortBy[0]
      const currentItem = sortBy[0]
      if (!currentItem || item.id !== currentItem?.id || item?.desc !== currentItem?.desc) {
        setSortBy([item])
      }
    }
  }, [tableProps.state, pagination, sortBy])

  const globalFilter: UsersGlobalFilter = tableProps.state.globalFilter

  tableProps.setGlobalFilter = useCallback(
    (filterValue: UsersGlobalFilter | ((prev: UsersGlobalFilter) => UsersGlobalFilter)) => {
      const newFilter = typeof filterValue === 'function' ? filterValue(globalFilter) : filterValue
      originalSetGlobalFilter(newFilter)
      setGlobalFilterCopy(newFilter)
      setPagination({ pageIndex: 0, pageSize: pagination.pageSize })
    },
    [originalSetGlobalFilter, globalFilter, pagination.pageSize]
  )

  const switchStatus = useCallback(
    (status: string) => {
      tableProps.setGlobalFilter({ ...globalFilter, status })
      setStatusToStorage(status)
    },
    [globalFilter, setStatusToStorage, tableProps]
  )

  const handleChangeSearch = useCallback(
    (search: string) => {
      tableProps.setGlobalFilter({ ...globalFilter, search })
      tableProps.gotoPage(0)
      setPagination({ pageIndex: 0, pageSize: pagination.pageSize })
    },
    [globalFilter, tableProps, pagination.pageSize]
  )

  const handleExportCsv = () => {
    const preparedData = prepareUsersCsvData(users?.items || [], Object.values(featureGroups || {}))
    const csvData = arrayToCSV(preparedData)
    downloadCsv(`users[${dayjs(new Date()).format('DD-MM-HHmmss')}].csv`, csvData)
  }

  const uploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = (e?.target?.files || [])[0]
    e.preventDefault()
    if (!file) return

    if (uploadRef.current) {
      uploadRef.current.value = ''
      uploadRef.current.type = 'text'
      uploadRef.current.type = 'file'
    }
    try {
      await importUsersFn(file, i_realm)
    } catch (err: any) {
      // @ts-ignore
      toast.error(err?.response?.data?.reason || t('toast.importFailed'))
    }
  }

  const handleImportCsv = () => {
    uploadRef?.current?.click()
  }

  return (
    <Page
      title={t('page.users.title')}
      filter={
        <AdvancedFilters
          i_realm={i_realm}
          globalFilter={globalFilter}
          setGlobalFilter={(filterValue) => {
            tableProps.setGlobalFilter({ ...filterValue })
            setPagination({ pageIndex: 0, pageSize: pagination.pageSize })
          }}
        />
      }
    >
      <input type="file" ref={uploadRef} style={{ display: 'none' }} onChange={uploadFile} />
      <PageHeader
        title={t('page.users.title')}
        subtitle={users?.total ?? ''}
        search={globalFilter.search}
        onChangeSearch={handleChangeSearch}
        onExportCsv={handleExportCsv}
        onImportCsv={handleImportCsv}
        actions={
          <div
            style={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-between',
              flexWrap: 'wrap-reverse',
              gap: '8px',
            }}
          >
            <Tabs activeTab={globalFilter.status} onSwitch={switchStatus}>
              <Tab label={t('common.active')} value="active" />
              <Tab label={t('common.pending')} value="pending" />
            </Tabs>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {selectedCount > 0 ? (
                <>
                  <Button icon={<Icon icon="uil:redo" />} onClick={() => tableProps.toggleAllRowsSelected(false)}>
                    {t('page.users.clearSelection')}
                  </Button>
                  <Button icon={<Icon icon="uil:trash" />} appearance="red" onClick={handleBulkDelete}>
                    {t('page.users.deleteSelection')} ({selectedCount})
                  </Button>
                </>
              ) : (
                <>
                  <LinkTo to={`/users?modal=${MODALS.deleteUser}`}>
                    <Button icon={<Icon icon="uil:trash" />} appearance="red">
                      {t('page.users.delete')}
                    </Button>
                  </LinkTo>

                  <LinkTo to={`/users?modal=${MODALS.addUser}`}>
                    <Button icon={<Icon icon="uil:plus" />} appearance="blue" style={{ marginLeft: '12px' }}>
                      {t('page.users.add')}
                    </Button>
                  </LinkTo>
                </>
              )}
            </div>
          </div>
        }
      />
      <Table isLoading={isLoading} initialFilter={initialGlobalFilter} {...tableProps} />
    </Page>
  )
}

export default Users
